import type { TimeInterval } from "../Types/schema-helpers";

export interface WorkingDaySchedule {
  dayIdx: number;
  working: boolean;
  intervals: TimeInterval[];
}

export interface IntervalError {
  startError: boolean;
  endError: boolean;
}

export interface DayScheduleError {
  intervals: IntervalError[];
  hasErrors: boolean;
}

export interface ScheduleValidationResult {
  isOverallValid: boolean;
  detailedErrors: DayScheduleError[];
}

/**
 * Converts 'HH:MM' string or number to total minutes from midnight for easy comparison.
 */
export const timeToMinutes = (time?: string | number | null): number => {
  if (time == null || time === "") return -1;
  if (typeof time === "number") return time;
  
  try {
    const parts = time.split(':');
    if (parts.length < 2) return -1;
    
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) return -1;
    return hours * 60 + minutes;
  } catch {
    return -1;
  }
};

/**
 * Converts total minutes from midnight to "HH:MM" string format.
 * e.g., 1020 -> "17:00", 0 -> "00:00"
 */
export const minutesToTime = (minutes?: number | null): string => {
  if (minutes == null || minutes < 0) return "";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
  return `${pad(hours)}:${pad(mins)}`;
};

/**
 * Extracts the time (HH:MM) from an ISO 8601 string (e.g., "2025-10-02T07:00:00").
 * This avoids timezone conversion issues by treating the time component as a string.
 */
export const formatTime = (dt?: string | null): string => {
  if (!dt) return "";
  const match = dt.match(/T(\d{2}:\d{2}):\d{2}/);
  return match && match[1] ? match[1] : "";
};

/**
 * Creates an ISO 8601 string with a fixed dummy date and the given time.
 * @returns ISO 8601 string (e.g., "2000-01-01T07:00:00") or null.
 */
export const buildDateTime = (time?: string | null): string | null => {
  if (!time) return null;
  return `2000-01-01T${time}:00`;
};

/**
 * Validates the entire weekly schedule structure for overlaps and correctness.
 */
export const validateSchedule = (
  currentSchedule: WorkingDaySchedule[]
): ScheduleValidationResult => {
  let isOverallValid = true;

  const detailedErrors: DayScheduleError[] = Array.from({ length: 7 }, () => ({
    intervals: [],
    hasErrors: false,
  }));

  currentSchedule.forEach((day: WorkingDaySchedule, dayIdx: number) => {
    if (!day.working) {
      return;
    }

    let prevEndMinutes = -1;
    let dayHasErrors = false;

    day.intervals.forEach((interval: TimeInterval, intervalIdx: number) => {
      const errors: IntervalError = { startError: false, endError: false };

      if (interval.start != null && interval.end != null) {
        const startMinutes = timeToMinutes(interval.start);
        const endMinutes = timeToMinutes(interval.end);

        // Rule 1: Start < End
        if (startMinutes === -1 || endMinutes === -1 || startMinutes >= endMinutes) {
          errors.startError = true;
          errors.endError = true;
          isOverallValid = false;
          dayHasErrors = true;
        }

        // Rules 2 & 3: No Overlap & Sorted
        if (prevEndMinutes !== -1 && startMinutes < prevEndMinutes) {
          errors.startError = true;
          isOverallValid = false;
          dayHasErrors = true;
        }

        // Update prevEndMinutes for the next comparison
        if (startMinutes < endMinutes) {
          prevEndMinutes = endMinutes;
        } else {
          prevEndMinutes = startMinutes;
        }
      }

      const currentDayError = detailedErrors[dayIdx];
      if (currentDayError) {
        currentDayError.intervals[intervalIdx] = errors;
      }
    });

    const currentDayError = detailedErrors[dayIdx];
    if (currentDayError) {
      currentDayError.hasErrors = dayHasErrors;
    }
  });

  return { isOverallValid, detailedErrors };
};